import { useState, useRef } from "react";
import { Button } from "./ui/button";
import { Upload, X, Loader2, Image as ImageIcon } from "lucide-react";

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function ImageUpload({ value, onChange, label = "Image" }) {
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState("");
  const fileRef = useRef(null);

  const handleUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (!file.type.startsWith("image/")) {
      setError("Only image files allowed");
      return;
    }
    if (file.size > 10 * 1024 * 1024) {
      setError("File too large (max 10MB)");
      return;
    }

    setError("");
    setUploading(true);
    try {
      const formData = new FormData();
      formData.append("file", file);
      const res = await fetch(`${API_URL}/api/upload`, {
        method: "POST",
        credentials: "include",
        body: formData,
      });
      if (!res.ok) {
        const data = await res.json();
        throw new Error(data.detail || "Upload failed");
      }
      const data = await res.json();
      onChange(`${API_URL}${data.url}`);
    } catch (err) {
      setError(err.message);
    } finally {
      setUploading(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  return (
    <div>
      <label className="text-stone-300 text-sm font-medium">{label}</label>
      <div className="mt-1 flex items-start gap-3">
        <div className="flex-1">
          <input
            ref={fileRef}
            type="file"
            accept="image/*"
            onChange={handleUpload}
            className="hidden"
            data-testid="image-upload-input"
          />
          <div className="flex gap-2">
            <input
              type="text"
              value={value || ""}
              onChange={(e) => onChange(e.target.value)}
              placeholder="Image URL or upload"
              className="flex-1 bg-stone-800 border border-stone-700 text-white text-sm rounded-xl h-10 px-3"
              data-testid="image-url-input"
            />
            <Button
              type="button"
              variant="outline"
              size="sm"
              disabled={uploading}
              onClick={() => fileRef.current?.click()}
              className="border-stone-700 text-stone-300 rounded-xl h-10 px-3"
              data-testid="image-upload-btn"
            >
              {uploading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Upload className="w-4 h-4" />}
            </Button>
            {value && (
              <Button
                type="button"
                variant="ghost"
                size="sm"
                onClick={() => onChange("")}
                className="text-stone-500 hover:text-red-400 h-10"
              >
                <X className="w-4 h-4" />
              </Button>
            )}
          </div>
          {error && <p className="text-red-400 text-xs mt-1">{error}</p>}
        </div>
        {value && (
          <img
            src={value}
            alt="Preview"
            className="w-16 h-12 rounded-lg object-cover border border-stone-700"
          />
        )}
      </div>
    </div>
  );
}
